import { CheckCircle as CheckCircleIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { progressStagesDialogHelpItems } from '../../utils/helpItems';
import HelpOverlay from '../common/HelpOverlay';
import { useTrackUsage } from '../../hooks/useTrackUsage';

// Define only the complete stages for display to users
const USER_VISIBLE_STAGES = [
    { value: 'LEVEL0_AI_COMPLETE', label: 'L0 - AI Complete', color: '#ff9800' },
    { value: 'LEVEL1_SEVAK_COMPLETE', label: 'L1 - Complete', color: '#2196f3' },
    { value: 'LEVEL2_SEVAK_COMPLETE', label: 'L2 - Complete', color: '#9c27b0' },
    { value: 'LEVEL3_SEVAK_COMPLETE', label: 'L3 - Complete', color: '#3f51b5' },
    { value: 'PUBLISHED', label: 'Published', color: '#4caf50' },
];

// Define internal stages for backend (including WIP stages)
const PROCESS_STAGES = [
    { value: 'LEVEL0_AI_WIP', label: 'L0 - AI In Progress', color: '#f44336', internal: true },
    { value: 'LEVEL0_AI_COMPLETE', label: 'L0 - AI Complete', color: '#ff9800', internal: false },
    { value: 'LEVEL1_WIP', label: 'L1 - Editing In Progress', color: '#ffeb3b', internal: true },
    { value: 'LEVEL1_SEVAK_COMPLETE', label: 'L1 - Complete', color: '#2196f3', internal: false },
    { value: 'LEVEL2_WIP', label: 'L2 - Review In Progress', color: '#03a9f4', internal: true },
    { value: 'LEVEL2_SEVAK_COMPLETE', label: 'L2 - Complete', color: '#9c27b0', internal: false },
    { value: 'LEVEL3_WIP', label: 'L3 - Final In Progress', color: '#673ab7', internal: true },
    { value: 'LEVEL3_SEVAK_COMPLETE', label: 'L3 - Complete', color: '#3f51b5', internal: false },
    { value: 'PUBLISHED', label: 'Published', color: '#4caf50', internal: false },
];

// Define stage progression - which user-visible stages can follow each other
const USER_STAGE_PROGRESSION = {
    LEVEL0_AI_COMPLETE: ['LEVEL1_SEVAK_COMPLETE'],
    LEVEL1_SEVAK_COMPLETE: ['LEVEL2_SEVAK_COMPLETE', 'LEVEL0_AI_COMPLETE'],
    LEVEL2_SEVAK_COMPLETE: ['LEVEL3_SEVAK_COMPLETE', 'LEVEL1_SEVAK_COMPLETE', 'LEVEL0_AI_COMPLETE'],
    LEVEL3_SEVAK_COMPLETE: ['PUBLISHED', 'LEVEL2_SEVAK_COMPLETE', 'LEVEL1_SEVAK_COMPLETE', 'LEVEL0_AI_COMPLETE'],
    PUBLISHED: ['LEVEL3_SEVAK_COMPLETE', 'LEVEL2_SEVAK_COMPLETE', 'LEVEL1_SEVAK_COMPLETE', 'LEVEL0_AI_COMPLETE'],
};

const ProgressStagesDialog = ({
    open,
    onClose,
    videoId,
    folderPath,
    onChangesMade, // Add this prop
}) => {
    useTrackUsage('ProgressStagesDialog', 'dialog', 'folder-browser', open);
    const [loading, setLoading] = useState(true);
    const [videoData, setVideoData] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [selectedStages, setSelectedStages] = useState({});
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [confirmingChanges, setConfirmingChanges] = useState({});
    const [updatingLanguages, setUpdatingLanguages] = useState({});
    const [updateSuccessful, setUpdateSuccessful] = useState({});
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => {
        if (open && videoId) {
            setLoading(true);
            setError('');
            setSuccessMessage('');
            setConfirmingChanges({});
            setUpdatingLanguages({});
            setUpdateSuccessful({});

            videoCRUDServiceInstance
                .getVideoById(videoId)
                .then((response) => {
                    const video = response.data;
                    setVideoData(video);

                    const langs = video.videoLanguages || [];
                    setLanguages(langs);

                    const currentStages = {};
                    langs.forEach((lang) => {
                        const rawStage = lang.processStage || 'LEVEL0_AI_WIP';
                        currentStages[lang.languageName] = convertToUserVisibleStage(rawStage);
                    });

                    setSelectedStages(currentStages);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error('Error fetching video details:', err);
                    setError('Failed to load video details. Please try again.');
                    setLoading(false);
                });
        }
    }, [open, videoId]);

    const convertToUserVisibleStage = (internalStage) => {
        if (!internalStage) return 'LEVEL0_AI_COMPLETE';

        if (internalStage.endsWith('WIP')) {
            if (internalStage === 'LEVEL0_AI_WIP') return 'LEVEL0_AI_COMPLETE';
            if (internalStage === 'LEVEL1_WIP') return 'LEVEL0_AI_COMPLETE';
            if (internalStage === 'LEVEL2_WIP') return 'LEVEL1_SEVAK_COMPLETE';
            if (internalStage === 'LEVEL3_WIP') return 'LEVEL2_SEVAK_COMPLETE';
        }

        if (internalStage.includes('COMPLETE') || internalStage === 'PUBLISHED') {
            return internalStage;
        }

        return 'LEVEL0_AI_COMPLETE';
    };

    const getNextWipStage = (completeStage) => {
        if (completeStage === 'LEVEL0_AI_COMPLETE') return 'LEVEL1_WIP';
        if (completeStage === 'LEVEL1_SEVAK_COMPLETE') return 'LEVEL2_WIP';
        if (completeStage === 'LEVEL2_SEVAK_COMPLETE') return 'LEVEL3_WIP';
        if (completeStage === 'LEVEL3_SEVAK_COMPLETE') return 'PUBLISHED';
        return null;
    };

    const handleStageChange = (languageName, newVisibleStage) => {
        // Update the selected stage
        setSelectedStages((prev) => ({
            ...prev,
            [languageName]: newVisibleStage,
        }));
    };

    const handleConfirmStageChange = (languageName, confirmed) => {
        if (confirmed) {
            // Get the selected stage for this language
            const selectedStage = selectedStages[languageName];

            // Start the update process
            updateProcessStage(languageName, selectedStage);
        }

        // Clear confirmation state
        setConfirmingChanges((prev) => ({
            ...prev,
            [languageName]: false,
        }));
    };

    const updateProcessStage = async (languageName, newVisibleStage) => {
        try {
            // Set updating state for this language
            setUpdatingLanguages((prev) => ({
                ...prev,
                [languageName]: true,
            }));

            // Clear any previous success state
            setUpdateSuccessful((prev) => ({
                ...prev,
                [languageName]: false,
            }));

            // Clear any global error/success
            setError('');
            setSuccessMessage('');

            const userData = JSON.parse(localStorage.getItem('authUser'));
            if (!userData) {
                setError('Authentication information not found. Please log in again.');
                setUpdatingLanguages((prev) => ({
                    ...prev,
                    [languageName]: false,
                }));
                return;
            }

            await videoCRUDServiceInstance.updateProcessStage(
                videoId,
                languageName,
                newVisibleStage,
                userData.email,
                userData.access,
            );

            const nextWipStage = getNextWipStage(newVisibleStage);

            if (nextWipStage && nextWipStage !== 'PUBLISHED') {
                await videoCRUDServiceInstance.updateProcessStage(
                    videoId,
                    languageName,
                    nextWipStage,
                    userData.email,
                    userData.access,
                );
            }

            // Fetch updated video data
            const response = await videoCRUDServiceInstance.getVideoById(videoId);
            setVideoData(response.data);

            const langs = response.data.videoLanguages || [];
            setLanguages(langs);

            // Update stages with fresh data
            const updatedStages = { ...selectedStages };
            langs.forEach((lang) => {
                if (lang.languageName === languageName) {
                    updatedStages[lang.languageName] = convertToUserVisibleStage(lang.processStage);
                }
            });
            setSelectedStages(updatedStages);

            // Notify that changes were made (call the callback)
            if (onChangesMade) onChangesMade();

            // Set success state
            setUpdateSuccessful((prev) => ({
                ...prev,
                [languageName]: true,
            }));

            // Clear success state after delay
            setTimeout(() => {
                setUpdateSuccessful((prev) => ({
                    ...prev,
                    [languageName]: false,
                }));
            }, 3000);
        } catch (err) {
            console.error('Error updating process stage:', err);
            setError(`Failed to update ${languageName} progress stage: ${err.message}`);
        } finally {
            // Always clear updating state
            setUpdatingLanguages((prev) => ({
                ...prev,
                [languageName]: false,
            }));
        }
    };

    const getStageLabel = (stageValue) => {
        const stage = PROCESS_STAGES.find((s) => s.value === stageValue);
        return stage ? stage.label : stageValue;
    };

    const getStageColor = (stageValue) => {
        const stage = PROCESS_STAGES.find((s) => s.value === stageValue);
        return stage ? stage.color : '#757575';
    };

    const getAllowedStages = (currentVisibleStage) => {
        if (!currentVisibleStage || !USER_STAGE_PROGRESSION[currentVisibleStage]) {
            return ['LEVEL0_AI_COMPLETE'];
        }
        return USER_STAGE_PROGRESSION[currentVisibleStage];
    };

    const isStageAllowed = (currentVisibleStage, targetVisibleStage) => {
        return getAllowedStages(currentVisibleStage).includes(targetVisibleStage);
    };

    const getNextSuggestedVisibleStage = (currentVisibleStage) => {
        if (currentVisibleStage === 'LEVEL0_AI_COMPLETE') return 'LEVEL1_SEVAK_COMPLETE';
        if (currentVisibleStage === 'LEVEL1_SEVAK_COMPLETE') return 'LEVEL2_SEVAK_COMPLETE';
        if (currentVisibleStage === 'LEVEL2_SEVAK_COMPLETE') return 'LEVEL3_SEVAK_COMPLETE';
        if (currentVisibleStage === 'LEVEL3_SEVAK_COMPLETE') return 'PUBLISHED';
        return null;
    };

    const isLanguageReady = (language) => {
        return language.status === 'READY' && language.vttFileUrl;
    };

    return (
        <>
        <Dialog open={open} onClose={() => !loading && onClose()} fullWidth maxWidth="md">
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" component="div">
                        Manage Progress Stages
                        {videoData && (
                            <Typography variant="subtitle1" color="textSecondary">
                                {videoData.videoName || videoData.name}
                            </Typography>
                        )}
                    </Typography>
                    <Tooltip title="Help Guide">
                        <IconButton size="small" onClick={() => setHelpOpen(true)}>
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <>
                        {successMessage && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {successMessage}
                            </Alert>
                        )}

                        <Box sx={{ mb: 3 }}>
                            <Box id="progress-stage-progression" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2">
                                    Stage Progression:
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                    mt: 1,
                                }}
                            >
                                {USER_VISIBLE_STAGES.map((stage, index) => (
                                    <React.Fragment key={stage.value}>
                                        <Box
                                            sx={{
                                                backgroundColor: stage.color,
                                                color: '#fff',
                                                borderRadius: '4px',
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {stage.label}
                                        </Box>

                                        {index < USER_VISIBLE_STAGES.length - 1 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    mx: 0.5,
                                                }}
                                            >
                                                <Box sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>→</Box>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        color: 'text.secondary',
                                                        mt: -0.5,
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    {index === 0
                                                        ? 'L1 In Progress'
                                                        : index === 1
                                                        ? 'L2 In Progress'
                                                        : index === 2
                                                        ? 'L3 In Progress'
                                                        : ''}
                                                </Typography>
                                            </Box>
                                        )}
                                    </React.Fragment>
                                ))}
                            </Box>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Stage Progression:</strong> As content moves through the workflow, progress
                                    stages track its status from Initial to Published. Each stage must be completed in
                                    sequence, though content can move back to earlier stages if needed.
                                    <em style={{ display: 'block', marginTop: '4px', fontSize: '0.9em' }}>
                                        Note: There are automatic "In Progress" stages created by the system between
                                        each main stage.
                                    </em>
                                </Typography>
                            </Alert>
                        </Box>

                        {languages.length === 0 ? (
                            <Typography color="textSecondary">No languages available for this video.</Typography>
                        ) : (
                            <Box id="progress-language-grid">
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={3}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Language
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Current Stage
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Transition To:
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            Actions
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ mb: 2 }} />

                                {languages.map((language) => {
                                    const currentInternalStage = language.processStage || 'LEVEL0_AI_WIP';
                                    const currentVisibleStage = convertToUserVisibleStage(currentInternalStage);
                                    const nextSuggestedStage = getNextSuggestedVisibleStage(currentVisibleStage);

                                    return (
                                        <Paper
                                            key={language.languageName}
                                            elevation={0}
                                            sx={{
                                                mb: 2,
                                                p: 1,
                                                bgcolor: 'background.paper',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={3}>
                                                    <Typography fontWeight="medium">{language.languageName}</Typography>
                                                    {!isLanguageReady(language) && (
                                                        <Typography variant="caption" color="error">
                                                            Not ready for editing
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                <Grid item xs={3}>
                                                    <Box
                                                        sx={{
                                                            display: 'inline-block',
                                                            backgroundColor: getStageColor(currentVisibleStage),
                                                            color: '#fff',
                                                            borderRadius: '4px',
                                                            px: 1,
                                                            py: 0.5,
                                                        }}
                                                    >
                                                        {getStageLabel(currentVisibleStage)}
                                                    </Box>
                                                </Grid>

                                                <Grid item xs={4}>
                                                    <FormControl
                                                        fullWidth
                                                        size="small"
                                                        disabled={
                                                            !isLanguageReady(language) ||
                                                            currentInternalStage === 'LEVEL0_AI_WIP'
                                                        }
                                                    >
                                                        <Select
                                                            value={
                                                                selectedStages[language.languageName] ||
                                                                currentVisibleStage
                                                            }
                                                            onChange={(e) =>
                                                                handleStageChange(language.languageName, e.target.value)
                                                            }
                                                        >
                                                            {USER_VISIBLE_STAGES.map((stage) => {
                                                                const isAllowed = isStageAllowed(
                                                                    currentVisibleStage,
                                                                    stage.value,
                                                                );
                                                                const isSuggested = stage.value === nextSuggestedStage;

                                                                return (
                                                                    <MenuItem
                                                                        key={stage.value}
                                                                        value={stage.value}
                                                                        disabled={
                                                                            !isAllowed ||
                                                                            stage.value === currentVisibleStage
                                                                        }
                                                                        sx={{
                                                                            ...(isSuggested && {
                                                                                fontWeight: 'bold',
                                                                                bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                                            }),
                                                                        }}
                                                                    >
                                                                        {stage.label}
                                                                        {isSuggested && ' (Suggested Next Stage)'}
                                                                    </MenuItem>
                                                                );
                                                            })}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={2}>
                                                    {confirmingChanges[language.languageName] ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="warning"
                                                                onClick={() =>
                                                                    handleConfirmStageChange(
                                                                        language.languageName,
                                                                        true,
                                                                    )
                                                                }
                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                            >
                                                                Yes
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="inherit"
                                                                onClick={() =>
                                                                    handleConfirmStageChange(
                                                                        language.languageName,
                                                                        false,
                                                                    )
                                                                }
                                                                sx={{ minWidth: 'auto', px: 1 }}
                                                            >
                                                                No
                                                            </Button>
                                                        </Box>
                                                    ) : updatingLanguages[language.languageName] ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            disabled
                                                            sx={{ textTransform: 'none' }}
                                                            startIcon={<CircularProgress size={16} />}
                                                        >
                                                            Updating...
                                                        </Button>
                                                    ) : updateSuccessful[language.languageName] ? (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            disableElevation
                                                            sx={{ textTransform: 'none' }}
                                                            startIcon={<CheckCircleIcon />}
                                                        >
                                                            Updated
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            disabled={
                                                                !isLanguageReady(language) ||
                                                                selectedStages[language.languageName] ===
                                                                    currentVisibleStage ||
                                                                currentInternalStage === 'LEVEL0_AI_WIP'
                                                            }
                                                            onClick={() => {
                                                                const nextSuggestedStage =
                                                                    getNextSuggestedVisibleStage(currentVisibleStage);

                                                                // If moving to suggested next stage, don't require confirmation
                                                                if (
                                                                    selectedStages[language.languageName] ===
                                                                    nextSuggestedStage
                                                                ) {
                                                                    updateProcessStage(
                                                                        language.languageName,
                                                                        selectedStages[language.languageName],
                                                                    );
                                                                } else {
                                                                    // Otherwise ask for confirmation
                                                                    setConfirmingChanges((prev) => ({
                                                                        ...prev,
                                                                        [language.languageName]: true,
                                                                    }));
                                                                }
                                                            }}
                                                        >
                                                            Update
                                                        </Button>
                                                    )}
                                                </Grid>

                                                {confirmingChanges[language.languageName] && (
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                                                            <Typography variant="body2">
                                                                Are you sure you want to change{' '}
                                                                <strong>{language.languageName}</strong> from{' '}
                                                                <strong>{getStageLabel(currentVisibleStage)}</strong> to{' '}
                                                                <strong>
                                                                    {getStageLabel(
                                                                        selectedStages[language.languageName],
                                                                    )}
                                                                </strong>
                                                                ? This will mark the current stage as complete.
                                                            </Typography>
                                                        </Alert>
                                                    </Grid>
                                                )}

                                                {error && error.includes(language.languageName) && (
                                                    <Grid item xs={12}>
                                                        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                                                            {error}
                                                        </Alert>
                                                    </Grid>
                                                )}

                                                {isLanguageReady(language) &&
                                                    currentInternalStage !== 'LEVEL0_AI_WIP' && (
                                                        <Box sx={{ mt: 1, pl: 2, gridColumn: '1 / -1' }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {currentVisibleStage !== 'PUBLISHED' ? (
                                                                    <>
                                                                        Current:{' '}
                                                                        <b>{getStageLabel(currentVisibleStage)}</b> →
                                                                        Suggested Next:{' '}
                                                                        <b>{getStageLabel(nextSuggestedStage)}</b>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        This language is <b>Published</b> and has
                                                                        completed all stages.
                                                                    </>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                            </Grid>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        <HelpOverlay
            helpItems={progressStagesDialogHelpItems}
            title="Process Dialog Guide"
            showButton={false}
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
        />
        </>
    );
};

export default ProgressStagesDialog;
